"use client";

import Button from "../Button";
import { SafeAvailabilityRule, SafeListing, SafeReservation, SafeUser } from "@/app/types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import useCurrencies from "@/app/hooks/useCurrencies";
import Counter from "../inputs/Counter";
import { toast } from "react-hot-toast";
import CalendarReservations from "../../components/inputs/CalendarReservations";
import FunDiveCalendar from "../../components/inputs/CalendarReservationsFunDives";
import { eachDayOfInterval, isSameDay, format, differenceInDays } from "date-fns";
import { BsLightningCharge } from "react-icons/bs";
import { IoMdTime } from "react-icons/io";
import { hoursLeftToBook, getMinBookableDate } from "@/app/utils/bookingLeadTime";
import { ExchangeCurrency } from "@prisma/client";
import useRegisterModal from "@/app/hooks/useRegisterModal";
import { Range } from "react-date-range";
import { generateConfirmationCode } from '@/app/libs/confirmationCode';
import formatPrice from "@/app/utils/formatPrice";
import { courseCancellationPolicy } from "@/app/utils/bookingPolicyDisplay";
import useAvailability, { getClientSpotsForDate } from "@/app/hooks/useAvailability";
import { dayKeyFromLocalDate } from "@/app/utils/calendarDay";
import Link from "next/link";
import { readBeginnerSwitchDraft } from "@/app/libs/beginnerSwitch";
import trackClientEvent from "@/app/libs/trackClientEvent";

const initialDateRange = {
  startDate: new Date(),
  endDate: new Date(),
  key: "selection",
};

interface ListingReservationProps {
  price: number;
  priceExtraDiver: number;
  priceExtraSnorkeler: number;
  priceNitrox: number;
  price15L: number;
  priceFullSet: number;
  priceComputer: number;
  priceTorch: number;
  priceAdapter: number;
  priceBCD: number;
  priceReg: number;
  priceWetsuit: number;
  priceMask: number;
  priceFins: number;
  priceWeights: number;
  pricePrivateGuide: number;
  priceFees: number;
  discountTotal: number;
  discountWeekly: number;
  disabled?: boolean;
  currentUser?: SafeUser | null;
  listing: SafeListing;
  reservations?: SafeReservation[];
  selectedCurrency?: any;
  rates: ExchangeCurrency | null;
  availabilityRules: SafeAvailabilityRule[];
  initialMonth: number;
  initialYear: number;
}

const ListingReservation: React.FC<ListingReservationProps> = ({
  price,
  priceExtraDiver,
  priceExtraSnorkeler,
  priceNitrox,
  price15L,
  priceFullSet,
  priceComputer,
  priceTorch,
  priceAdapter,
  priceBCD,
  priceReg,
  priceWetsuit,
  priceMask,
  priceFins,
  priceWeights,
  pricePrivateGuide,
  priceFees,
  discountTotal,
  discountWeekly,
  disabled,
  currentUser,
  listing,
  selectedCurrency,
  availabilityRules,
  initialMonth,
  initialYear
}) => {
  const router = useRouter();
  const { getByCode } = useCurrencies();
  const currentCode = getByCode(selectedCurrency?.code || currentUser?.currency || "USD");
  const newCurrencySymbol = currentCode?.symbol || "$";
  const registerModal = useRegisterModal();

  // Extras past the first few stay folded so the calendar is not pushed far
  // down the page (this used to be a small inner scroll area).
  const extrasRef = useRef<HTMLDivElement>(null);
  const [showAllExtras, setShowAllExtras] = useState(false);
  const [extrasCount, setExtrasCount] = useState(0);
  useEffect(() => {
    setExtrasCount(extrasRef.current?.children.length ?? 0);
  });

  const [diverCount, setDiverCount] = useState(1);
  const [extraDiverCount, setExtraDiverCount] = useState(0);
  const [extraSnorkelerCount, setExtraSnorkelerCount] = useState(0);
  const [nitroxCount, setNitroxCount] = useState(0);
  const [l15Count, setl15Count] = useState(0);
  const [fullSetCount, setFullSetCount] = useState(0);
  const [computerCount, setComputerCount] = useState(0);
  const [torchCount, setTorchCount] = useState(0);
  const [adapterCount, setAdapterCount] = useState(0);
  const [BCDCount, setBCDCount] = useState(0);
  const [regCount, setRegCount] = useState(0);
  const [wetsuitCount, setWetsuitCount] = useState(0);
  const [maskCount, setMaskCount] = useState(0);
  const [finsCount, setFinsCount] = useState(0);
  const [weightsCount, setWeightsCount] = useState(0);
  const [guideCount, setGuideCount] = useState(0);

  const [counterPrice, setCounterPrice] = useState(price);
  const [counterPriceExtra, setCounterPriceExtra] = useState(priceExtraDiver);
  const [counterPriceExtraSnorkeler, setCounterPriceExtraSnorkeler] = useState(priceExtraSnorkeler);
  const [counterPriceNitrox, setCounterPriceNitrox] = useState(priceNitrox);
  const [counterPrice15L, setCounterPrice15L] = useState(price15L);
  const [counterPriceFullSet, setCounterPriceFullSet] = useState(priceFullSet);
  const [counterPriceComputer, setCounterPriceComputer] = useState(priceComputer);
  const [counterPriceTorch, setCounterPriceTorch] = useState(priceTorch);
  const [counterPriceAdapter, setCounterPriceAdapter] = useState(priceAdapter);
  const [counterPriceBCD, setCounterPriceBCD] = useState(priceBCD);
  const [counterPriceReg, setCounterPriceReg] = useState(priceReg);
  const [counterPriceWetsuit, setCounterPriceWetsuit] = useState(priceWetsuit);
  const [counterPriceMask, setCounterPriceMask] = useState(priceMask);
  const [counterPriceFins, setCounterPriceFins] = useState(priceFins);
  const [counterPriceWeights, setCounterPriceWeights] = useState(priceWeights);
  const [counterPriceGuide, setCounterPriceGuide] = useState(pricePrivateGuide);
  const [totalFees, setTotalFees] = useState(priceFees);
  const [totalPrice, setTotalPrice] = useState(price);
  const [totalPriceAfterDiscount, setTotalPriceAfterDiscount] = useState(0);
  const [totalPriceAfterFees, setTotalPriceAfterFees] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  /** Children of the extras list shown before "Show all extras". */
  const EXTRAS_SHOWN = 4;

  const feesIncluded = listing?.included?.includes('Fees');
  const feesNotIncluded = listing?.notIncluded?.includes('Fees');
  // Google TTD price policy: every displayed price must already include
  // mandatory fees. Display-only uplift; checkout math is unchanged.
  const upfrontFee = feesNotIncluded ? priceFees : 0;

  // Coming from "Book this instead" when the original date was not bookable here:
  // keep the group and gear, only the date is missing.
  const [pickDateNote, setPickDateNote] = useState<string | null>(null);
  useEffect(() => {
    const draft = readBeginnerSwitchDraft(listing.id);
    if (!draft?.needsDate) return;
    setDiverCount(draft.counts.participants);
    setExtraDiverCount(draft.counts.extraDiver);
    setExtraSnorkelerCount(draft.counts.extraSnorkeler);
    setNitroxCount(draft.equipment.nitrox);
    setl15Count(draft.equipment.l15);
    setFullSetCount(draft.equipment.fullSet);
    setComputerCount(draft.equipment.computer);
    setTorchCount(draft.equipment.torch);
    setAdapterCount(draft.equipment.adapter);
    setBCDCount(draft.equipment.bcd);
    setRegCount(draft.equipment.reg);
    setWetsuitCount(draft.equipment.wetsuit);
    setMaskCount(draft.equipment.mask);
    setFinsCount(draft.equipment.fins);
    setWeightsCount(draft.equipment.weights);
    setGuideCount(draft.equipment.privateGuide);
    setPickDateNote(`Pick a date for ${draft.listingTitle}`);
  }, [listing.id]);

  const [dateRange, setDateRange] = useState<Range>(initialDateRange);
  /** Avoid fetching availability for default "today" before the hook picks the first bookable date. */
  const [pickDateReady, setPickDateReady] = useState(false);


  const {
    availableDates,
    availableSpots,
    disabledDates,
    selectedDate,
    selectedDateData,
    spotsParticipants,
    spotsExtraDivers,
    spotsExtraSnorkelers,
    isDateValid,
    currentMonth,
    currentYear,
    firstAvailableDate,
    isLoading: availabilityLoading,
    setSelectedDate,
    handleMonthChange,
    fetchAvailabilityForDate,
    fetchAvailabilityForDateRange
  } = useAvailability({
    listingId: listing.id,
    availabilityRules,
    initialMonth,
    initialYear,
    startTime: listing.startTime,
    leadHours: (listing as any).minBookingLeadHours
  });

  useEffect(() => {
    const calculatedPrice = price * diverCount;
    setCounterPrice(calculatedPrice);

    let calculatedPriceExtra = 0;
    let calculatedPriceExtraSnorkeler = 0;
    let calculatedPriceNitrox = 0;
    let calculatedPrice15L = 0;
    let calculatedPriceFullSet = 0;
    let calculatedPriceComputer = 0;
    let calculatedPriceTorch = 0;
    let calculatedPriceAdapter = 0;
    let calculatedPriceBCD = 0;
    let calculatedPriceReg = 0;
    let calculatedPriceWetsuit = 0;
    let calculatedPriceMask = 0;
    let calculatedPriceFins = 0;
    let calculatedPriceWeights = 0;
    let calculatedPriceGuide = 0;
    let calculatedFees = 0;

    if (priceExtraDiver != 0 && extraDiverCount === 0) {
      setCounterPriceExtra(priceExtraDiver);
    } else {
      calculatedPriceExtra = priceExtraDiver * extraDiverCount;
      setCounterPriceExtra(calculatedPriceExtra);
    }

    if (priceExtraSnorkeler != 0 && extraSnorkelerCount === 0) {
      setCounterPriceExtraSnorkeler(priceExtraSnorkeler);
    } else {
      calculatedPriceExtraSnorkeler = priceExtraSnorkeler * extraSnorkelerCount;
      setCounterPriceExtraSnorkeler(calculatedPriceExtraSnorkeler);
    }

    if (priceNitrox != 0 && nitroxCount === 0) {
      setCounterPriceNitrox(priceNitrox);
    } else {
      calculatedPriceNitrox = priceNitrox * nitroxCount;
      setCounterPriceNitrox(calculatedPriceNitrox);
    }

    if (price15L != 0 && l15Count === 0) {
      setCounterPrice15L(price15L);
    } else {
      calculatedPrice15L = price15L * l15Count;
      setCounterPrice15L(calculatedPrice15L);
    }

    if (priceFullSet != 0 && fullSetCount === 0) {
      setCounterPriceFullSet(priceFullSet);
    } else {
      calculatedPriceFullSet = priceFullSet * fullSetCount;
      setCounterPriceFullSet(calculatedPriceFullSet);
    }

    if (priceComputer != 0 && computerCount === 0) {
      setCounterPriceComputer(priceComputer);
    } else {
      calculatedPriceComputer = priceComputer * computerCount;
      setCounterPriceComputer(calculatedPriceComputer);
    }

    if (priceTorch != 0 && torchCount === 0) {
      setCounterPriceTorch(priceTorch);
    } else {
      calculatedPriceTorch = priceTorch * torchCount;
      setCounterPriceTorch(calculatedPriceTorch);
    }

    if (priceAdapter != 0 && adapterCount === 0) {
      setCounterPriceAdapter(priceAdapter);
    } else {
      calculatedPriceAdapter = priceAdapter * adapterCount;
      setCounterPriceAdapter(calculatedPriceAdapter);
    }

    if (priceBCD != 0 && BCDCount === 0) {
      setCounterPriceBCD(priceBCD);
    } else {
      calculatedPriceBCD = priceBCD * BCDCount;
      setCounterPriceBCD(calculatedPriceBCD);
    }

    if (priceReg != 0 && regCount === 0) {
      setCounterPriceReg(priceReg);
    } else {
      calculatedPriceReg = priceReg * regCount;
      setCounterPriceReg(calculatedPriceReg);
    }

    if (priceWetsuit != 0 && wetsuitCount === 0) {
      setCounterPriceWetsuit(priceWetsuit);
    } else {
      calculatedPriceWetsuit = priceWetsuit * wetsuitCount;
      setCounterPriceWetsuit(calculatedPriceWetsuit);
    }

    if (priceMask != 0 && maskCount === 0) {
      setCounterPriceMask(priceMask);
    } else {
      calculatedPriceMask = priceMask * maskCount;
      setCounterPriceMask(calculatedPriceMask);
    }

    if (priceFins != 0 && finsCount === 0) {
      setCounterPriceFins(priceFins);
    } else {
      calculatedPriceFins = priceFins * finsCount;
      setCounterPriceFins(calculatedPriceFins);
    }

    if (priceWeights != 0 && weightsCount === 0) {
      setCounterPriceWeights(priceWeights);
    } else {
      calculatedPriceWeights = priceWeights * weightsCount;
      setCounterPriceWeights(calculatedPriceWeights);
    }

    if (pricePrivateGuide != 0 && guideCount === 0) {
      setCounterPriceGuide(pricePrivateGuide);
    } else {
      calculatedPriceGuide = pricePrivateGuide * guideCount;
      setCounterPriceGuide(calculatedPriceGuide);
    }

    if (priceFees && diverCount) {
      calculatedFees = priceFees * diverCount;
      setTotalFees(calculatedFees);
    } else {
      setTotalFees(0);
    }

    const daysInDateRange = differenceInDays(dateRange.endDate as Date, dateRange.startDate as Date) + 1;

    let fullPrice = 0;
    const priceDays = calculatedPrice * daysInDateRange;
    const priceExtraDays = calculatedPriceExtra * daysInDateRange;
    const priceExtraSnorkelDays = calculatedPriceExtraSnorkeler * daysInDateRange;

    if (daysInDateRange <= 1) {
      fullPrice = calculatedPrice + calculatedPriceExtra + calculatedPriceExtraSnorkeler + calculatedPriceNitrox + calculatedPrice15L + calculatedPriceFullSet + calculatedPriceComputer + calculatedPriceTorch + calculatedPriceAdapter + calculatedPriceBCD + calculatedPriceReg + calculatedPriceWetsuit + calculatedPriceMask + calculatedPriceFins + calculatedPriceWeights + calculatedPriceGuide;
      setTotalPrice(fullPrice);
    } else {
      fullPrice = priceDays + priceExtraDays + priceExtraSnorkelDays + calculatedPriceNitrox + calculatedPrice15L + calculatedPriceFullSet + calculatedPriceComputer + calculatedPriceTorch + calculatedPriceAdapter + calculatedPriceBCD + calculatedPriceReg + calculatedPriceWetsuit + calculatedPriceMask + calculatedPriceFins + calculatedPriceWeights + calculatedPriceGuide;
      setTotalPrice(fullPrice);
    }
  }, [diverCount, extraDiverCount, extraSnorkelerCount, nitroxCount, l15Count, fullSetCount, computerCount, torchCount, adapterCount, BCDCount, regCount, wetsuitCount, maskCount, finsCount, weightsCount, guideCount, price, priceExtraDiver, priceExtraSnorkeler, priceFullSet, priceComputer, priceTorch, priceAdapter, priceBCD, priceReg, priceWetsuit, priceMask, priceFins, priceWeights, pricePrivateGuide, priceFees, dateRange]);

  let discountedPrice = 0;
  let priceAfterFees = 0;

  const isRangeMoreThanAWeek = (startDate: Date, endDate: Date): boolean => {
    const differenceDays = differenceInDays(endDate, startDate) + 1;
    return differenceDays >= 7;
  };

  const isMoreThanAWeek = dateRange.startDate && dateRange.endDate ?
    isRangeMoreThanAWeek(dateRange.startDate, dateRange.endDate) :
    false;

  useEffect(() => {
    if (discountTotal === null && discountTotal === 0 && discountWeekly !== null && discountWeekly !== 0 && isMoreThanAWeek) {
      discountedPrice = totalPrice - (totalPrice * discountWeekly / 100);
      setTotalPriceAfterDiscount(discountedPrice);
    } else if (discountTotal != 0 && discountTotal != null) {
      discountedPrice = totalPrice - (totalPrice * discountTotal / 100);
      setTotalPriceAfterDiscount(discountedPrice);
    }

    if (feesNotIncluded && discountedPrice != 0) {
      priceAfterFees = discountedPrice + totalFees;
      setTotalPriceAfterFees(priceAfterFees);
    } else if (feesNotIncluded && discountedPrice === 0) {
      priceAfterFees = totalPrice + totalFees;
      setTotalPriceAfterFees(priceAfterFees);
    }

  }, [totalPrice, discountedPrice]);

  const getIndividualDatesFromRange = (range: Range): Date[] | undefined => {
    const { startDate, endDate } = range;
    if (startDate && endDate) {
      return eachDayOfInterval({ start: startDate, end: endDate });
    }
    return undefined;
  };

  const individualDates = useMemo(() => {
    const dates = getIndividualDatesFromRange(dateRange);
    if (dates && dates.length > 0) {
      const uniqueDates = Array.from(new Set(dates.map(date => date.getTime())))
        .map(time => new Date(time));
      return uniqueDates;
    }

    return dates;
  }, [dateRange]);

  useEffect(() => {
    // FUN DIVES included: the range used to start at today, which the 36h
    // lead time forbids, so an untouched Reserve always errored. The range
    // is also corrected when it still sits on the unbookable initial today
    // (pickDateReady can win a race against the availability fetch).
    if (firstAvailableDate) {
      const start = dateRange.startDate;
      const stillOnUnbookableToday =
        !!start &&
        isSameDay(start, new Date()) &&
        !isSameDay(firstAvailableDate, start);
      if (!pickDateReady || stillOnUnbookableToday) {
        setDateRange({
          startDate: firstAvailableDate,
          endDate: firstAvailableDate,
          key: "selection",
        });
        setPickDateReady(true);
      }
      return;
    }
    if (!pickDateReady && !availabilityLoading && availableDates.length === 0) {
      setPickDateReady(true);
    }
  }, [
    firstAvailableDate,
    availableDates.length,
    availabilityLoading,
    listing?.category,
    pickDateReady,
    dateRange.startDate,
  ]);

  useEffect(() => {
    const runEffect = async () => {
      if (listing?.category === "FUN DIVES") {
        if (individualDates && individualDates.length > 0) {
          await fetchAvailabilityForDateRange(individualDates);
        }
        return;
      }

      if (!pickDateReady || !dateRange.startDate) return;

      const targetDate = dateRange.startDate;
      setSelectedDate(targetDate);
      await fetchAvailabilityForDate(targetDate);
    };

    void runEffect();
  }, [
    pickDateReady,
    dateRange.startDate?.getTime(),
    individualDates,
    listing?.category,
    fetchAvailabilityForDate,
    fetchAvailabilityForDateRange,
    setSelectedDate,
  ]);

  const onNewReservation = useCallback(() => {
    if (diverCount === 0) {
      toast.error("Number of divers can't be 0!");
      return;
    }

    setIsLoading(true);

    const confirmationCode = generateConfirmationCode();

    if (!isDateValid) {
      toast.error("The selected date is not available.");
      setIsLoading(false);
      return;
    }

    let dates: Date[] | undefined;

    if (listing?.category === "FUN DIVES") {
      dates = individualDates;

      if (!dates || dates.length === 0) {
        toast.error("Please select valid dates for your reservation.");
        setIsLoading(false);
        return;
      }

      const hasDisabledDates = dates.some(date =>
        disabledDates.some(disabledDate => isSameDay(disabledDate, date))
      );

      if (hasDisabledDates) {
        toast.error("One or more selected dates are not allowed. Same-day reservations are not allowed.");
        setIsLoading(false);
        return;
      }

      // Validate against the availability rules directly. availableDates only
      // covers the currently viewed month (and FunDiveCalendar never updates
      // currentMonth), so ranges in a later month would always fail here.
      const minBookable = getMinBookableDate(
        listing?.startTime,
        (listing as any)?.minBookingLeadHours
      );
      const allDatesAvailable = dates.every(date => {
        if (date < minBookable) return false;
        const spots = getClientSpotsForDate(date, availabilityRules);
        return !!spots && spots.participants > 0;
      });

      if (!allDatesAvailable) {
        toast.error("One or more selected dates are not available.");
        setIsLoading(false);
        return;
      }
    } else {
      if (!selectedDate) {
        toast.error("Please select a date for your reservation.");
        setIsLoading(false);
        return;
      }

      dates = [selectedDate];

      const hasDisabledDates = dates.some(date =>
        disabledDates.some(disabledDate => isSameDay(disabledDate, date))
      );

      if (hasDisabledDates) {
        toast.error("One or more selected dates are not allowed. Same-day reservations are not allowed.");
        setIsLoading(false);
        return;
      }

      const isDateAvailable = availableDates.some(availableDate =>
        isSameDay(availableDate, selectedDate)
      );

      if (!isDateAvailable) {
        toast.error("The selected date is not available.");
        setIsLoading(false);
        return;
      }
    }

    const hasEnoughSpots = diverCount <= spotsParticipants &&
      (priceExtraDiver === 0 || extraDiverCount <= spotsExtraDivers) &&
      (priceExtraSnorkeler === 0 || extraSnorkelerCount <= spotsExtraSnorkelers);

    if (!hasEnoughSpots) {
      toast.error("Maximum number of participants or extra divers reached for this date.");
      setIsLoading(false);
      return;
    }

    const status = listing?.automaticApproval === true ? "accepted" : "pending";

    const reservationData = {
      totalPrice,
      // Must match the currency ListingClient converted the prices into
      currency: selectedCurrency?.code || currentUser?.currency || "USD",
      // Calendar days of the dive center as "YYYY-MM-DD", never instants:
      // an ISO timestamp would carry the diver's own timezone.
      dates: dates?.map(date => dayKeyFromLocalDate(date)),
      participants: diverCount,
      extraDiver: extraDiverCount,
      extraSnorkeler: extraSnorkelerCount,
      nitrox: nitroxCount,
      l15: l15Count,
      fullSet: fullSetCount,
      computer: computerCount,
      torch: torchCount,
      adapter: adapterCount,
      bcd: BCDCount,
      reg: regCount,
      wetsuit: wetsuitCount,
      mask: maskCount,
      fins: finsCount,
      weights: weightsCount,
      privateGuide: guideCount,
      totalFees,
      selectedDate: selectedDate ? dayKeyFromLocalDate(selectedDate) : null,
      status,
      confirmationCode,
      totalPriceAfterDiscount,
      totalPriceAfterFees,
    };

    const urlWithParams = `/booking/${confirmationCode}/${listing.id}?reservationData=${encodeURIComponent(JSON.stringify(reservationData))}`;

    trackClientEvent("booking_start", {
      listingId: listing.id,
      metadata: {
        confirmationCode,
        dates: reservationData.dates,
        participants: diverCount,
        totalPrice,
        currency: reservationData.currency,
        category: listing?.category,
      },
    });

    // Same tab: a new tab gets killed by popup blockers (Safari/iOS) and
    // loses the visitor between the listing and the checkout.
    router.push(urlWithParams);
    setIsLoading(false);
  }, [
    diverCount, extraDiverCount, extraSnorkelerCount, nitroxCount, l15Count,
    fullSetCount, computerCount, torchCount, adapterCount, BCDCount, regCount, wetsuitCount,
    maskCount, finsCount, weightsCount, guideCount,
    isDateValid, individualDates, listing?.category, listing?.automaticApproval,
    listing?.startTime, availabilityRules,
    listing.id, selectedDate, availableDates, spotsParticipants, spotsExtraDivers,
    spotsExtraSnorkelers, priceExtraDiver, priceExtraSnorkeler, totalPrice,
    totalFees, totalPriceAfterDiscount, totalPriceAfterFees, selectedCurrency?.code, currentUser?.currency,
    router
  ]);

  return (
    <div>
      {pickDateNote && (
        <p className="mb-4 rounded-lg border border-cyan-100 bg-cyan-50 px-3 py-2 text-sm font-semibold text-cyan-800">
          {pickDateNote}
        </p>
      )}
      <div className="flex flex-col mb-4 rounded-xl border-[1px] border-neutral-200 bg-white p-4">
        <div className="flex flex-row items-center gap-2">
          <p className="font-bold text-black uppercase">PARTICIPANTS</p>
        </div>

        <div className="text-lg font-semibold">
          <div className="flex flex-col gap-2 py-2.5">
            <div className="flex flex-row items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <Counter
                  inline
                  onChange={(value) => setDiverCount(value)}
                  value={diverCount}
                  title="Diver"
                />
              </div>
              <div className="min-w-[3.75rem] flex-shrink-0 text-base font-bold flex items-center justify-end whitespace-nowrap">
                {discountTotal ? (
                  <div className="flex flex-row">
                    <del className="mr-2 font-light text-neutral-600">
                      {counterPrice !== null ? formatPrice(counterPrice + upfrontFee) : 'Loading...'}
                      {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                        <span >{selectedCurrency?.code || currentUser?.currency}</span>
                      )}
                    </del>
                    <span>
                      {((currentUser || !selectedCurrency?.symbol) ? newCurrencySymbol : selectedCurrency.symbol || "$")}&nbsp;
                      {(counterPrice !== null && discountTotal !== null) ? formatPrice(counterPrice - (counterPrice * discountTotal / 100) + upfrontFee) : 'Loading...'}
                      {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                        <span className="font-light text-xs">{selectedCurrency?.code || currentUser?.currency}</span>
                      )}
                    </span>
                  </div>
                ) : (
                  <>
                    {((currentUser || !selectedCurrency?.symbol) ? newCurrencySymbol : selectedCurrency.symbol || "$")}&nbsp;
                    {counterPrice !== null ? formatPrice(counterPrice + upfrontFee) : 'Loading...'}
                    {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                      <span className="font-light text-xs">{selectedCurrency?.code || currentUser?.currency}</span>
                    )}
                  </>
                )}
              </div>
            </div>
            {selectedDateData != undefined && diverCount > spotsParticipants && (
              <div className="text-red-500 text-xs">There are only {spotsParticipants} spots left on that date!</div>
            )}
          </div>
        </div>

        {priceExtraDiver != 0 && (
          <div className="text-lg font-semibold">
            <div className="flex flex-col gap-2 py-2.5">
              <div className="flex flex-row items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <Counter
                  inline
                    onChange={(value) => setExtraDiverCount(value)}
                    value={extraDiverCount}
                    title="Extra certified diver"
                  />
                </div>
                <div className="min-w-[3.75rem] flex-shrink-0 text-base font-bold flex items-center justify-end whitespace-nowrap">
                  {discountTotal ? (
                    <div className="flex flex-row">
                      <del className="mr-2 font-light text-neutral-600">
                        {counterPriceExtra !== null ? formatPrice(counterPriceExtra) : 'Loading...'}
                        {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                          <span >{selectedCurrency?.code || currentUser?.currency}</span>
                        )}
                      </del>
                      <span>
                        {((currentUser || !selectedCurrency?.symbol) ? newCurrencySymbol : selectedCurrency.symbol || "$")}&nbsp;
                        {(counterPriceExtra !== null && discountTotal !== null) ? formatPrice(counterPriceExtra - (counterPriceExtra * discountTotal / 100)) : 'Loading...'}
                        {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                          <span className="font-light text-xs">{selectedCurrency?.code || currentUser?.currency}</span>
                        )}
                      </span>
                    </div>
                  ) : (
                    <>
                      {((currentUser || !selectedCurrency?.symbol) ? newCurrencySymbol : selectedCurrency.symbol || "$")}&nbsp;
                      {counterPriceExtra !== null ? formatPrice(counterPriceExtra) : 'Loading...'}
                      {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                        <span className="font-light text-xs">{selectedCurrency?.code || currentUser?.currency}</span>
                      )}
                    </>
                  )}
                </div>
              </div>
              {selectedDateData != undefined && extraDiverCount > spotsExtraDivers && (
                <div className="text-red-500 text-xs">There are only {spotsExtraDivers} spots left on that date!</div>
              )}
            </div>
          </div>
        )}

        {priceExtraSnorkeler != 0 && (
          <div className="text-lg font-semibold">
            <div className="flex flex-col gap-2 py-2.5">
              <div className="flex flex-row items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <Counter
                  inline
                    onChange={(value) => setExtraSnorkelerCount(value)}
                    value={extraSnorkelerCount}
                    title="Extra snorkeler"
                  />
                </div>
                <div className="min-w-[3.75rem] flex-shrink-0 text-base font-bold flex items-center justify-end whitespace-nowrap">
                  {discountTotal ? (
                    <div className="flex flex-row">
                      <del className="mr-2 font-light text-neutral-600">
                        {counterPriceExtraSnorkeler !== null ? formatPrice(counterPriceExtraSnorkeler) : 'Loading...'}
                        {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                          <span >{selectedCurrency?.code || currentUser?.currency}</span>
                        )}
                      </del>
                      <span>
                        {((currentUser || !selectedCurrency?.symbol) ? newCurrencySymbol : selectedCurrency.symbol || "$")}&nbsp;
                        {(counterPriceExtraSnorkeler !== null && discountTotal !== null) ? formatPrice(counterPriceExtraSnorkeler - (counterPriceExtraSnorkeler * discountTotal / 100)) : 'Loading...'}
                        {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                          <span className="font-light text-xs">{selectedCurrency?.code || currentUser?.currency}</span>
                        )}
                      </span>
                    </div>
                  ) : (
                    <>
                      {((currentUser || !selectedCurrency?.symbol) ? newCurrencySymbol : selectedCurrency.symbol || "$")}&nbsp;
                      {counterPriceExtraSnorkeler !== null ? formatPrice(counterPriceExtraSnorkeler) : 'Loading...'}
                      {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                        <span className="font-light text-xs">{selectedCurrency?.code || currentUser?.currency}</span>
                      )}
                    </>
                  )}
                </div>
              </div>
              {selectedDateData != undefined && extraSnorkelerCount > spotsExtraSnorkelers && (
                <div className="text-red-500 text-xs">There are only {spotsExtraSnorkelers} spots left on that date!</div>
              )}
            </div>
          </div>
        )}
        {(priceNitrox != 0 || price15L != 0 || priceFullSet != 0 || priceComputer != 0 || priceTorch != 0 || priceAdapter != 0 || priceBCD != 0 || priceReg != 0 || priceWetsuit != 0 || priceMask != 0 || priceFins != 0 || priceWeights != 0 || pricePrivateGuide != 0) && (
          <>
            {(priceNitrox != 0 || price15L != 0) && (
              <div className="mt-3 text-xs font-bold uppercase tracking-wider text-cyan-700">ADD-ON (add 1 per day/diver)</div>
            )}
            <div
              ref={extrasRef}
              className={showAllExtras ? "" : "[&>*:nth-child(n+5)]:hidden"}
            >
              {priceNitrox != 0 && counterPriceNitrox != null && (
                <div className="text-lg font-semibold">
                  <div className="flex flex-col gap-2 py-2.5">
                    <div className="flex flex-row items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <Counter
                  inline
                          onChange={(value) => setNitroxCount(value)}
                          value={nitroxCount}
                          title="Nitrox tank"
                        />
                      </div>
                      <div className="min-w-[3.75rem] flex-shrink-0 text-base font-bold flex items-center justify-end whitespace-nowrap">
                        {discountTotal ? (
                          <div className="flex flex-row">
                            <del className="mr-2 font-light text-neutral-600">
                              {counterPriceNitrox !== null ? formatPrice(counterPriceNitrox) : 'Loading...'}
                              {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                                <span >{selectedCurrency?.code || currentUser?.currency}</span>
                              )}
                            </del>
                            <span>
                              {((currentUser || !selectedCurrency?.symbol) ? newCurrencySymbol : selectedCurrency.symbol || "$")}&nbsp;
                              {(counterPriceNitrox !== null && discountTotal !== null) ? formatPrice(counterPriceNitrox - (counterPriceNitrox * discountTotal / 100)) : 'Loading...'}
                              {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                                <span className="font-light text-xs">{selectedCurrency?.code || currentUser?.currency}</span>
                              )}
                            </span>
                          </div>
                        ) : (
                          <>
                            {((currentUser || !selectedCurrency?.symbol) ? newCurrencySymbol : selectedCurrency.symbol || "$")}&nbsp;
                            {counterPriceNitrox !== null ? formatPrice(counterPriceNitrox) : 'Loading...'}
                            {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                              <span className="font-light text-xs">{selectedCurrency?.code || currentUser?.currency}</span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}


              {price15L != 0 && counterPrice15L != null && (
                <div className="text-lg font-semibold">
                  <div className="flex flex-col gap-2 py-2.5">
                    <div className="flex flex-row items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <Counter
                  inline
                          onChange={(value) => setl15Count(value)}
                          value={l15Count}
                          title="Extra 15L tank"
                        />
                      </div>
                      <div className="min-w-[3.75rem] flex-shrink-0 text-base font-bold flex items-center justify-end whitespace-nowrap">
                        {discountTotal ? (
                          <div className="flex flex-row">
                            <del className="mr-2 font-light text-neutral-600">
                              {counterPrice15L !== null ? formatPrice(counterPrice15L) : 'Loading...'}
                              {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                                <span >{selectedCurrency?.code || currentUser?.currency}</span>
                              )}
                            </del>
                            <span>
                              {((currentUser || !selectedCurrency?.symbol) ? newCurrencySymbol : selectedCurrency.symbol || "$")}&nbsp;
                              {(counterPrice15L !== null && discountTotal !== null) ? formatPrice(counterPrice15L - (counterPrice15L * discountTotal / 100)) : 'Loading...'}
                              {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                                <span className="font-light text-xs">{selectedCurrency?.code || currentUser?.currency}</span>
                              )}
                            </span>
                          </div>
                        ) : (
                          <>
                            {((currentUser || !selectedCurrency?.symbol) ? newCurrencySymbol : selectedCurrency.symbol || "$")}&nbsp;
                            {counterPrice15L !== null ? formatPrice(counterPrice15L) : 'Loading...'}
                            {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                              <span className="font-light text-xs">{selectedCurrency?.code || currentUser?.currency}</span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {pricePrivateGuide != 0 && pricePrivateGuide != null && (
                <div className="text-lg font-semibold">
                  <div className="flex flex-col gap-2 py-2.5">
                    <div className="flex flex-row items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <Counter
                  inline
                          onChange={(value) => setGuideCount(value)}
                          value={guideCount}
                          title="Private guide"
                        />
                      </div>
                      <div className="min-w-[3.75rem] flex-shrink-0 text-base font-bold flex items-center justify-end whitespace-nowrap">
                        {discountTotal ? (
                          <div className="flex flex-row">
                            <del className="mr-2 font-light text-neutral-600">
                              {counterPriceGuide !== null ? formatPrice(counterPriceGuide) : 'Loading...'}
                              {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                                <span >{selectedCurrency?.code || currentUser?.currency}</span>
                              )}
                            </del>
                            <span>
                              {((currentUser || !selectedCurrency?.symbol) ? newCurrencySymbol : selectedCurrency.symbol || "$")}&nbsp;
                              {(counterPriceGuide !== null && discountTotal !== null) ? formatPrice(counterPriceGuide - (counterPriceGuide * discountTotal / 100)) : 'Loading...'}
                              {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                                <span className="font-light text-xs">{selectedCurrency?.code || currentUser?.currency}</span>
                              )}
                            </span>
                          </div>
                        ) : (
                          <>
                            {((currentUser || !selectedCurrency?.symbol) ? newCurrencySymbol : selectedCurrency.symbol || "$")}&nbsp;
                            {counterPriceGuide !== null ? formatPrice(counterPriceGuide) : 'Loading...'}
                            {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                              <span className="font-light text-xs">{selectedCurrency?.code || currentUser?.currency}</span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-3 border-t border-neutral-100 pt-4 text-xs font-bold uppercase tracking-wider text-neutral-500">Gear rental</div>

              {priceFullSet != 0 && counterPriceFullSet != null && (
                <div className="text-lg font-semibold">
                  <div className="flex flex-col gap-2 py-2.5">
                    <div className="flex flex-row items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <Counter
                  inline
                          onChange={(value) => setFullSetCount(value)}
                          value={fullSetCount}
                          title="Full set"
                        />
                      </div>
                      <div className="min-w-[3.75rem] flex-shrink-0 text-base font-bold flex items-center justify-end whitespace-nowrap">
                        {discountTotal ? (
                          <div className="flex flex-row">
                            <del className="mr-2 font-light text-neutral-600">
                              {counterPriceFullSet !== null ? formatPrice(counterPriceFullSet) : 'Loading...'}
                              {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                                <span >{selectedCurrency?.code || currentUser?.currency}</span>
                              )}
                            </del>
                            <span>
                              {((currentUser || !selectedCurrency?.symbol) ? newCurrencySymbol : selectedCurrency.symbol || "$")}&nbsp;
                              {(counterPriceFullSet !== null && discountTotal !== null) ? formatPrice(counterPriceFullSet - (counterPriceFullSet * discountTotal / 100)) : 'Loading...'}
                              {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                                <span className="font-light text-xs">{selectedCurrency?.code || currentUser?.currency}</span>
                              )}
                            </span>
                          </div>
                        ) : (
                          <>
                            {((currentUser || !selectedCurrency?.symbol) ? newCurrencySymbol : selectedCurrency.symbol || "$")}&nbsp;
                            {counterPriceFullSet !== null ? formatPrice(counterPriceFullSet) : 'Loading...'}
                            {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                              <span className="font-light text-xs">{selectedCurrency?.code || currentUser?.currency}</span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {priceComputer != 0 && counterPriceComputer != null && (
                <div className="text-lg font-semibold">
                  <div className="flex flex-col gap-2 py-2.5">
                    <div className="flex flex-row items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <Counter
                  inline
                          onChange={(value) => setComputerCount(value)}
                          value={computerCount}
                          title="Computer"
                        />
                      </div>
                      <div className="min-w-[3.75rem] flex-shrink-0 text-base font-bold flex items-center justify-end whitespace-nowrap">
                        {discountTotal ? (
                          <div className="flex flex-row">
                            <del className="mr-2 font-light text-neutral-600">
                              {counterPriceComputer !== null ? formatPrice(counterPriceComputer) : 'Loading...'}
                              {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                                <span >{selectedCurrency?.code || currentUser?.currency}</span>
                              )}
                            </del>
                            <span>
                              {((currentUser || !selectedCurrency?.symbol) ? newCurrencySymbol : selectedCurrency.symbol || "$")}&nbsp;
                              {(counterPriceComputer !== null && discountTotal !== null) ? formatPrice(counterPriceComputer - (counterPriceComputer * discountTotal / 100)) : 'Loading...'}
                              {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                                <span className="font-light text-xs">{selectedCurrency?.code || currentUser?.currency}</span>
                              )}
                            </span>
                          </div>
                        ) : (
                          <>
                            {((currentUser || !selectedCurrency?.symbol) ? newCurrencySymbol : selectedCurrency.symbol || "$")}&nbsp;
                            {counterPriceComputer !== null ? formatPrice(counterPriceComputer) : 'Loading...'}
                            {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                              <span className="font-light text-xs">{selectedCurrency?.code || currentUser?.currency}</span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {priceTorch != 0 && counterPriceTorch != null && (
                <div className="text-lg font-semibold">
                  <div className="flex flex-col gap-2 py-2.5">
                    <div className="flex flex-row items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <Counter
                  inline
                          onChange={(value) => setTorchCount(value)}
                          value={torchCount}
                          title="Dive torch"
                        />
                      </div>
                      <div className="min-w-[3.75rem] flex-shrink-0 text-base font-bold flex items-center justify-end whitespace-nowrap">
                        {discountTotal ? (
                          <div className="flex flex-row">
                            <del className="mr-2 font-light text-neutral-600">
                              {counterPriceTorch !== null ? formatPrice(counterPriceTorch) : 'Loading...'}
                              {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                                <span >{selectedCurrency?.code || currentUser?.currency}</span>
                              )}
                            </del>
                            <span>
                              {((currentUser || !selectedCurrency?.symbol) ? newCurrencySymbol : selectedCurrency.symbol || "$")}&nbsp;
                              {(counterPriceTorch !== null && discountTotal !== null) ? formatPrice(counterPriceTorch - (counterPriceTorch * discountTotal / 100)) : 'Loading...'}
                              {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                                <span className="font-light text-xs">{selectedCurrency?.code || currentUser?.currency}</span>
                              )}
                            </span>
                          </div>
                        ) : (
                          <>
                            {((currentUser || !selectedCurrency?.symbol) ? newCurrencySymbol : selectedCurrency.symbol || "$")}&nbsp;
                            {counterPriceTorch !== null ? formatPrice(counterPriceTorch) : 'Loading...'}
                            {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                              <span className="font-light text-xs">{selectedCurrency?.code || currentUser?.currency}</span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {priceAdapter != 0 && counterPriceAdapter != null && (
                <div className="text-lg font-semibold">
                  <div className="flex flex-col gap-2 py-2.5">
                    <div className="flex flex-row items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <Counter
                  inline
                          onChange={(value) => setAdapterCount(value)}
                          value={adapterCount}
                          title="Adapter"
                        />
                      </div>
                      <div className="min-w-[3.75rem] flex-shrink-0 text-base font-bold flex items-center justify-end whitespace-nowrap">
                        {discountTotal ? (
                          <div className="flex flex-row">
                            <del className="mr-2 font-light text-neutral-600">
                              {counterPriceAdapter !== null ? formatPrice(counterPriceAdapter) : 'Loading...'}
                              {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                                <span >{selectedCurrency?.code || currentUser?.currency}</span>
                              )}
                            </del>
                            <span>
                              {((currentUser || !selectedCurrency?.symbol) ? newCurrencySymbol : selectedCurrency.symbol || "$")}&nbsp;
                              {(counterPriceAdapter !== null && discountTotal !== null) ? formatPrice(counterPriceAdapter - (counterPriceAdapter * discountTotal / 100)) : 'Loading...'}
                              {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                                <span className="font-light text-xs">{selectedCurrency?.code || currentUser?.currency}</span>
                              )}
                            </span>
                          </div>
                        ) : (
                          <>
                            {((currentUser || !selectedCurrency?.symbol) ? newCurrencySymbol : selectedCurrency.symbol || "$")}&nbsp;
                            {counterPriceAdapter !== null ? formatPrice(counterPriceAdapter) : 'Loading...'}
                            {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                              <span className="font-light text-xs">{selectedCurrency?.code || currentUser?.currency}</span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {priceBCD != 0 && counterPriceBCD != null && (
                <div className="text-lg font-semibold">
                  <div className="flex flex-col gap-2 py-2.5">
                    <div className="flex flex-row items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <Counter
                  inline
                          onChange={(value) => setBCDCount(value)}
                          value={BCDCount}
                          title="BCD"
                        />
                      </div>
                      <div className="min-w-[3.75rem] flex-shrink-0 text-base font-bold flex items-center justify-end whitespace-nowrap">
                        {discountTotal ? (
                          <div className="flex flex-row">
                            <del className="mr-2 font-light text-neutral-600">
                              {counterPriceBCD !== null ? formatPrice(counterPriceBCD) : 'Loading...'}
                              {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                                <span >{selectedCurrency?.code || currentUser?.currency}</span>
                              )}
                            </del>
                            <span>
                              {((currentUser || !selectedCurrency?.symbol) ? newCurrencySymbol : selectedCurrency.symbol || "$")}&nbsp;
                              {(counterPriceBCD !== null && discountTotal !== null) ? formatPrice(counterPriceBCD - (counterPriceBCD * discountTotal / 100)) : 'Loading...'}
                              {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                                <span className="font-light text-xs">{selectedCurrency?.code || currentUser?.currency}</span>
                              )}
                            </span>
                          </div>
                        ) : (
                          <>
                            {((currentUser || !selectedCurrency?.symbol) ? newCurrencySymbol : selectedCurrency.symbol || "$")}&nbsp;
                            {counterPriceBCD !== null ? formatPrice(counterPriceBCD) : 'Loading...'}
                            {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                              <span className="font-light text-xs">{selectedCurrency?.code || currentUser?.currency}</span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {priceReg != 0 && counterPriceReg != null && (
                <div className="text-lg font-semibold">
                  <div className="flex flex-col gap-2 py-2.5">
                    <div className="flex flex-row items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <Counter
                  inline
                          onChange={(value) => setRegCount(value)}
                          value={regCount}
                          title="Regulator"
                        />
                      </div>
                      <div className="min-w-[3.75rem] flex-shrink-0 text-base font-bold flex items-center justify-end whitespace-nowrap">
                        {discountTotal ? (
                          <div className="flex flex-row">
                            <del className="mr-2 font-light text-neutral-600">
                              {counterPriceReg !== null ? formatPrice(counterPriceReg) : 'Loading...'}
                              {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                                <span >{selectedCurrency?.code || currentUser?.currency}</span>
                              )}
                            </del>
                            <span>
                              {((currentUser || !selectedCurrency?.symbol) ? newCurrencySymbol : selectedCurrency.symbol || "$")}&nbsp;
                              {(counterPriceReg !== null && discountTotal !== null) ? formatPrice(counterPriceReg - (counterPriceReg * discountTotal / 100)) : 'Loading...'}
                              {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                                <span className="font-light text-xs">{selectedCurrency?.code || currentUser?.currency}</span>
                              )}
                            </span>
                          </div>
                        ) : (
                          <>
                            {((currentUser || !selectedCurrency?.symbol) ? newCurrencySymbol : selectedCurrency.symbol || "$")}&nbsp;
                            {counterPriceReg !== null ? formatPrice(counterPriceReg) : 'Loading...'}
                            {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                              <span className="font-light text-xs">{selectedCurrency?.code || currentUser?.currency}</span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {priceWetsuit != 0 && counterPriceWetsuit != null && (
                <div className="text-lg font-semibold">
                  <div className="flex flex-col gap-2 py-2.5">
                    <div className="flex flex-row items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <Counter
                  inline
                          onChange={(value) => setWetsuitCount(value)}
                          value={wetsuitCount}
                          title="Wetsuit"
                        />
                      </div>
                      <div className="min-w-[3.75rem] flex-shrink-0 text-base font-bold flex items-center justify-end whitespace-nowrap">
                        {discountTotal ? (
                          <div className="flex flex-row">
                            <del className="mr-2 font-light text-neutral-600">
                              {counterPriceWetsuit !== null ? formatPrice(counterPriceWetsuit) : 'Loading...'}
                              {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                                <span >{selectedCurrency?.code || currentUser?.currency}</span>
                              )}
                            </del>
                            <span>
                              {((currentUser || !selectedCurrency?.symbol) ? newCurrencySymbol : selectedCurrency.symbol || "$")}&nbsp;
                              {(counterPriceWetsuit !== null && discountTotal !== null) ? formatPrice(counterPriceWetsuit - (counterPriceWetsuit * discountTotal / 100)) : 'Loading...'}
                              {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                                <span className="font-light text-xs">{selectedCurrency?.code || currentUser?.currency}</span>
                              )}
                            </span>
                          </div>
                        ) : (
                          <>
                            {((currentUser || !selectedCurrency?.symbol) ? newCurrencySymbol : selectedCurrency.symbol || "$")}&nbsp;
                            {counterPriceWetsuit !== null ? formatPrice(counterPriceWetsuit) : 'Loading...'}
                            {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                              <span className="font-light text-xs">{selectedCurrency?.code || currentUser?.currency}</span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {priceMask != 0 && counterPriceMask != null && (
                <div className="text-lg font-semibold">
                  <div className="flex flex-col gap-2 py-2.5">
                    <div className="flex flex-row items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <Counter
                  inline
                          onChange={(value) => setMaskCount(value)}
                          value={maskCount}
                          title="Mask"
                        />
                      </div>
                      <div className="min-w-[3.75rem] flex-shrink-0 text-base font-bold flex items-center justify-end whitespace-nowrap">
                        {discountTotal ? (
                          <div className="flex flex-row">
                            <del className="mr-2 font-light text-neutral-600">
                              {counterPriceMask !== null ? formatPrice(counterPriceMask) : 'Loading...'}
                              {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                                <span >{selectedCurrency?.code || currentUser?.currency}</span>
                              )}
                            </del>
                            <span>
                              {((currentUser || !selectedCurrency?.symbol) ? newCurrencySymbol : selectedCurrency.symbol || "$")}&nbsp;
                              {(counterPriceMask !== null && discountTotal !== null) ? formatPrice(counterPriceMask - (counterPriceMask * discountTotal / 100)) : 'Loading...'}
                              {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                                <span className="font-light text-xs">{selectedCurrency?.code || currentUser?.currency}</span>
                              )}
                            </span>
                          </div>
                        ) : (
                          <>
                            {((currentUser || !selectedCurrency?.symbol) ? newCurrencySymbol : selectedCurrency.symbol || "$")}&nbsp;
                            {counterPriceMask !== null ? formatPrice(counterPriceMask) : 'Loading...'}
                            {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                              <span className="font-light text-xs">{selectedCurrency?.code || currentUser?.currency}</span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {priceFins != 0 && counterPriceFins != null && (
                <div className="text-lg font-semibold">
                  <div className="flex flex-col gap-2 py-2.5">
                    <div className="flex flex-row items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <Counter
                  inline
                          onChange={(value) => setFinsCount(value)}
                          value={finsCount}
                          title="Fins"
                        />
                      </div>
                      <div className="min-w-[3.75rem] flex-shrink-0 text-base font-bold flex items-center justify-end whitespace-nowrap">
                        {discountTotal ? (
                          <div className="flex flex-row">
                            <del className="mr-2 font-light text-neutral-600">
                              {counterPriceFins !== null ? formatPrice(counterPriceFins) : 'Loading...'}
                              {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                                <span >{selectedCurrency?.code || currentUser?.currency}</span>
                              )}
                            </del>
                            <span>
                              {((currentUser || !selectedCurrency?.symbol) ? newCurrencySymbol : selectedCurrency.symbol || "$")}&nbsp;
                              {(counterPriceFins !== null && discountTotal !== null) ? formatPrice(counterPriceFins - (counterPriceFins * discountTotal / 100)) : 'Loading...'}
                              {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                                <span className="font-light text-xs">{selectedCurrency?.code || currentUser?.currency}</span>
                              )}
                            </span>
                          </div>
                        ) : (
                          <>
                            {((currentUser || !selectedCurrency?.symbol) ? newCurrencySymbol : selectedCurrency.symbol || "$")}&nbsp;
                            {counterPriceFins !== null ? formatPrice(counterPriceFins) : 'Loading...'}
                            {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                              <span className="font-light text-xs">{selectedCurrency?.code || currentUser?.currency}</span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {priceWeights != 0 && counterPriceWeights != null && (
                <div className="text-lg font-semibold">
                  <div className="flex flex-col gap-2 py-2.5">
                    <div className="flex flex-row items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <Counter
                  inline
                          onChange={(value) => setWeightsCount(value)}
                          value={weightsCount}
                          title="Weights"
                        />
                      </div>
                      <div className="min-w-[3.75rem] flex-shrink-0 text-base font-bold flex items-center justify-end whitespace-nowrap">
                        {discountTotal ? (
                          <div className="flex flex-row">
                            <del className="mr-2 font-light text-neutral-600">
                              {counterPriceWeights !== null ? formatPrice(counterPriceWeights) : 'Loading...'}
                              {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                                <span >{selectedCurrency?.code || currentUser?.currency}</span>
                              )}
                            </del>
                            <span>
                              {((currentUser || !selectedCurrency?.symbol) ? newCurrencySymbol : selectedCurrency.symbol || "$")}&nbsp;
                              {(counterPriceWeights !== null && discountTotal !== null) ? formatPrice(counterPriceWeights - (counterPriceWeights * discountTotal / 100)) : 'Loading...'}
                              {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                                <span className="font-light text-xs">{selectedCurrency?.code || currentUser?.currency}</span>
                              )}
                            </span>
                          </div>
                        ) : (
                          <>
                            {((currentUser || !selectedCurrency?.symbol) ? newCurrencySymbol : selectedCurrency.symbol || "$")}&nbsp;
                            {counterPriceWeights !== null ? formatPrice(counterPriceWeights) : 'Loading...'}
                            {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                              <span className="font-light text-xs">{selectedCurrency?.code || currentUser?.currency}</span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {extrasCount > EXTRAS_SHOWN && (
              <button
                type="button"
                onClick={() => setShowAllExtras((open) => !open)}
                className="mt-1 self-start text-sm font-semibold text-cyan-800 underline underline-offset-2 hover:text-cyan-600"
              >
                {showAllExtras
                  ? "Show fewer extras"
                  : `Show all extras (${extrasCount - EXTRAS_SHOWN} more)`}
              </button>
            )}
          </>
        )}

        {discountWeekly !== null && discountWeekly != 0 && (!discountTotal || discountTotal === 0 || discountTotal === null) && (
          <div className="mt-1 text-sm text-green-600 font-light">Book a minimum of 7 days to enjoy a {discountWeekly}% discount</div>
        )}
      </div>

      <div id="book-now" className="bg-white rounded-xl border-[1px] border-neutral-200 overflow-hidden">
        <div className="flex flex-col gap-1 p-4">
          <div className="font-bold text-black uppercase">Pick a date</div>
          <div className="font-light text-neutral-600">
            {/* Next available date: <span className="font-medium text-neutral-800"> {nextAvailableDate ? format(nextAvailableDate, "MMMM d, yyyy") : "N/A"}</span> */}
            Next available date: <span className="font-medium text-neutral-800"> {firstAvailableDate ? format(firstAvailableDate, "MMMM d, yyyy") : "N/A"}</span>
          </div>
        </div>
        <hr />
        <div className="p-4">
          {listing?.category === "FUN DIVES" ? (
            <FunDiveCalendar
              value={dateRange}
              onChange={(value) => setDateRange(value)}
              disabledDates={disabledDates}
              minDate={getMinBookableDate(listing?.startTime, (listing as any)?.minBookingLeadHours)}
              availabilityRules={availabilityRules}
              loading={isLoading}
            />
          ) : (
            <CalendarReservations
              value={dateRange}
              onChange={(value) => setDateRange(value)}
              disabledDates={disabledDates}
              minDate={getMinBookableDate(listing?.startTime, (listing as any)?.minBookingLeadHours)}
              availabilityRules={availabilityRules}
              onMonthChange={handleMonthChange}
              loading={isLoading}
              currentMonth={currentMonth}
              currentYear={currentYear}
            />
          )}
          {(() => {
            const sel = dateRange?.startDate;
            if (!sel) return null;
            const left = hoursLeftToBook(
              sel,
              listing?.startTime,
              (listing as any)?.minBookingLeadHours
            );
            if (left == null) return null;
            return (
              <div className="flex items-center gap-1.5 mt-3 text-sm font-medium text-red-400">
                <IoMdTime size={17} />
                <span>
                  {left} {left === 1 ? "hour" : "hours"} left to book
                </span>
              </div>
            );
          })()}
        </div>

        <hr />
        <div className="p-4">
          {currentUser?.userType !== "Center" ? (
            <Button disabled={!isDateValid || disabled} label="Reserve" onClick={onNewReservation} />

          ) : (
            <div>
              <p className="mb-2 text-green-700">Create a diver account to start booking experiences!</p>
              <Button label="Create an account" onClick={() => registerModal.onOpen()} />
            </div>
          )}
        </div>
        {listing?.automaticApproval === true && (
          <div className="px-4 text-sm">
            <div className="flex items-center mr-2">
              <BsLightningCharge />
              <span className="ml-1">Instant confirmation</span>
            </div>
            <div className="text-orange-700 font-light">This reservation will be approved automatically</div>
          </div>
        )}
        <div className="px-4 text-sm mt-2">
          {(() => {
            const policy = courseCancellationPolicy(!!listing?.isNonRefundable);
            return (
              <>
                <div className="font-medium text-gray-800">{policy.label}</div>
                <div className="text-gray-600 font-light">{policy.summary}</div>
              </>
            );
          })()}
        </div>
        <hr />
        <div className="p-4 flex flex-col justify-between">
          <div className="flex flex-row justify-between items-center font-semibold text-lg">
            <div>Total</div>
            <div className="flex items-center">
              {(discountTotal !== null && discountTotal !== 0) || ((discountWeekly !== null && discountWeekly !== 0) && isMoreThanAWeek) ? (
                <>
                  <del className="mr-2 font-light text-neutral-600">
                    {((currentUser || !selectedCurrency?.symbol) ? newCurrencySymbol : selectedCurrency.symbol || "$")}&nbsp;
                    {formatPrice(totalPrice + (feesNotIncluded ? totalFees : 0))}
                    {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                      <span className="font-light text-xs">{selectedCurrency?.code || currentUser?.currency}</span>
                    )}
                  </del>
                  <span>
                    {((currentUser || !selectedCurrency?.symbol) ? newCurrencySymbol : selectedCurrency.symbol || "$")}&nbsp;
                    {totalPriceAfterDiscount !== null ? formatPrice(totalPriceAfterDiscount + (feesNotIncluded ? totalFees : 0)) : 'Loading...'}
                    {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                      <span className="font-light text-xs">{selectedCurrency?.code || currentUser?.currency}</span>
                    )}
                  </span>
                </>
              ) : (
                <span>
                  {((currentUser || !selectedCurrency?.symbol) ? newCurrencySymbol : selectedCurrency.symbol || "$")}&nbsp;
                  {totalPrice !== null ? formatPrice(totalPrice + (feesNotIncluded ? totalFees : 0)) : 'Loading...'}
                  {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                    <span className="font-light text-xs">{selectedCurrency?.code || currentUser?.currency}</span>
                  )}
                </span>
              )}
            </div>
          </div>
          {priceFees !== 0 && (
            <>
              {feesIncluded && (
                <div className="flex justify-end font-light text-sm">
                  <span>Includes {((currentUser || !selectedCurrency?.symbol) ? newCurrencySymbol : selectedCurrency.symbol || "$")}&nbsp;{formatPrice(totalFees)} in fees</span>
                </div>
              )}
              {feesNotIncluded && (
                <div className="flex justify-end font-light text-sm">
                  <span>Includes {((currentUser || !selectedCurrency?.symbol) ? newCurrencySymbol : selectedCurrency.symbol || "$")}&nbsp;{formatPrice(totalFees)} in fees</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ListingReservation;
